import * as React from 'react';
import { forwardRef, Ref, useMemo, useState } from 'react';
import ExcelExportButton from 'components/common/file/ExcelExportButton';
import DataTableHeader from 'components/common/table/datatable/DataTableHeader';
import DataTableLayout from 'components/common/table/datatable/DataTableLayout';
import NewDataTableBody from 'components/common/table/datatable/NewDataTableBody';
import { InvoicePaymentStatus, PaymentMethod } from 'types/plansales/billing';
import {
  GetInvoiceHistoryItem,
  GetInvoiceHistoryResponse,
} from 'types/plansales/invoice/invoiceHistory';
import { getTableRowNumber } from 'modules/dataTable';
import {
  paymentInvoiceDateCellRenderer,
  PayStatusStyle,
  RouteText,
} from '../../billing/billingtable/DataTableCellRenderer';
import {
  groupPlanMap,
  paymentInvoiceStatusLabel,
  paymentMethodLabel,
} from 'constants/plansales/billing';
import classNames from 'classnames';
import { TableCell } from 'types/common/table';
import { Button } from '@material-ui/core';
import { UnfoldLess, UnfoldMore } from '@material-ui/icons';
import styled from '@emotion/styled';

interface Props {
  dateInterval: string[];
  searchResultData: GetInvoiceHistoryResponse;
  onClickExport: () => void;
  searchResultDataLoading: boolean;
  fetchResultData: (pageNumber: number) => void;
  pageNo: number;
  isFetchingExcelData: boolean;
  excelData: any[];
}

const InvoiceHistoryList = forwardRef(
  (
    {
      dateInterval: [dateFrom, dateTo],
      searchResultData,
      onClickExport,
      isFetchingExcelData,
      excelData,
      searchResultDataLoading,
      fetchResultData,
      pageNo,
    }: Props,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const [isListFolding, setIsListFolding] = useState<boolean>(true);

    const fileName = `closet_invoices_${dateFrom}_${dateTo}`.replace(new RegExp('-', 'g'), '_');

    const columns = useMemo<TableCell<GetInvoiceHistoryItem>[]>(() => {
      const defaultColumns: TableCell<GetInvoiceHistoryItem>[] = [
        {
          align: 'center',
          name: 'No.',
          key: 'no',
          render: (rowData: GetInvoiceHistoryItem, _: any, rowIndex: number) =>
            getTableRowNumber(
              searchResultData.totalItems,
              50,
              searchResultData.currentPage,
              rowIndex,
            ),
        },
        {
          align: 'left',
          name: 'Invoice No.',
          key: 'invoiceNo',
          render: (rowData: GetInvoiceHistoryItem) => (
            <RouteText to={`/sales/invoices/${rowData.invoiceNo}`}>{rowData.invoiceNo}</RouteText>
          ),
        },
        {
          align: 'left',
          name: 'Group ID',
          key: 'groupId',
          render: (rowData: GetInvoiceHistoryItem) => (
            <RouteText to={`/groups/${rowData?.groupId}`}>{rowData.groupId}</RouteText>
          ),
        },
        {
          align: 'left',
          name: 'Group Name',
          key: 'groupName',
          render: (rowData: GetInvoiceHistoryItem) => rowData.groupName,
        },
        {
          align: 'left',
          name: 'Admin Email',
          key: 'adminEmail',
          render: (rowData: GetInvoiceHistoryItem) => (
            <RouteText to={`/member/${rowData?.adminUserId}/general`}>
              {rowData.adminEmail}
            </RouteText>
          ),
        },
        {
          align: 'left',
          name: 'Plan Type',
          key: 'groupType',
          width: '100px',
          render: (rowData: GetInvoiceHistoryItem) => groupPlanMap[rowData.planType],
        },
        {
          align: 'center',
          name: 'Payment Method',
          key: 'paymentMethod',
          width: '100px',
          render: (rowData: GetInvoiceHistoryItem) =>
            paymentMethodLabel[rowData.paymentMethod as PaymentMethod],
        },
        {
          align: 'right',
          name: 'Billed Amount (Including tax)',
          key: 'billedAmount',
          width: '100px',
          render: (rowData: GetInvoiceHistoryItem) =>
            (rowData.totalAmount + rowData.taxAmount).toFixed(2),
        },
        {
          align: 'right',
          name: 'Payment by Credit',
          key: 'paymentByCredit',
          width: '100px',
          render: (rowData: GetInvoiceHistoryItem) => rowData.creditAmount.toFixed(2),
        },
        {
          align: 'right',
          name: 'Payment by Card (Including tax)',
          key: 'paymentByCard',
          width: '100px',
          render: (rowData: GetInvoiceHistoryItem) =>
            (rowData.cardAmount + rowData.taxAmount).toFixed(2),
        },
        {
          align: 'center',
          name: 'Pay Status',
          key: 'payStatus',
          render: (rowData: GetInvoiceHistoryItem) => (
            <PayStatusStyle
              className={classNames({
                emphasized: [
                  InvoicePaymentStatus.Refunded,
                  InvoicePaymentStatus.Uncollectible,
                  InvoicePaymentStatus.None,
                ].includes(rowData.paymentStatus),
              })}
            >
              {paymentInvoiceStatusLabel[rowData.paymentStatus as InvoicePaymentStatus]}
            </PayStatusStyle>
          ),
        },
        {
          align: 'left',
          name: 'Payment Date',
          key: 'paymentDate',
          render: (rowData: GetInvoiceHistoryItem) =>
            paymentInvoiceDateCellRenderer(rowData.paymentSuccessAt, rowData.paymentStatus),
        },
      ];

      if (isListFolding) {
        return defaultColumns;
      }

      return [
        ...defaultColumns,
        {
          align: 'left',
          name: 'PaymentTxId',
          key: 'PaymentTxId',
          width: '100px',
          render: (rowData: GetInvoiceHistoryItem) => (
            <LineBreakText>{rowData.paymentInfo.paymentTaxId}</LineBreakText>
          ),
        },
        {
          align: 'left',
          name: 'Avalara Doc Code',
          key: 'AvalaraDocCode',
          width: '100px',
          render: (rowData: GetInvoiceHistoryItem) => rowData.paymentInfo.avalaraDocCode,
        },
      ];
    }, [isListFolding]);

    return (
      <DataTableLayout
        showPagination
        totalPages={searchResultData.totalPages}
        onChangePage={fetchResultData}
        currentPageIndex={searchResultData.currentPage}
        upperButton={[
          <ExcelExportButton
            key={'excelExport'}
            ref={ref}
            width={120}
            buttonText={'Export List'}
            fileName={fileName}
            exportData={excelData}
            exportLoading={isFetchingExcelData}
            onClickExport={onClickExport}
          />,
          <Button
            variant={'outlined'}
            key={'extend'}
            onClick={() => setIsListFolding(!isListFolding)}
          >
            <RotateIcon>{isListFolding ? <UnfoldMore /> : <UnfoldLess />}</RotateIcon>
          </Button>,
        ]}
      >
        <DataTableHeader header={columns} />
        <NewDataTableBody<GetInvoiceHistoryItem>
          dataList={searchResultData.items ?? []}
          columns={columns}
          loading={searchResultDataLoading}
        />
      </DataTableLayout>
    );
  },
);

const LineBreakText = styled.div`
  line-break: anywhere;
`;

const RotateIcon = styled.span`
  height: 24px;
  transform: rotate(90deg);
`;

export default InvoiceHistoryList;
